import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, Share2, FileText, Upload, Download, Trash2, Plus, ExternalLink, Video } from "lucide-react";
import { initials, ageDisplay, fmtDate, fmtDateTime, displayTime } from "@/lib/format";
import BookAppointmentDialog from "@/components/dialogs/BookAppointmentDialog";
import ReferPatientDialog from "@/components/dialogs/ReferPatientDialog";
import VideoCallDialog, { meetingLinkFor } from "@/components/VideoCallDialog";
import { useAuth } from "@/lib/auth";

export default function PatientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [appts, setAppts] = useState([]);
  const [reports, setReports] = useState([]);
  const [consults, setConsults] = useState([]);
  const [bills, setBills] = useState([]);
  const [tab, setTab] = useState("overview");
  const [openBook, setOpenBook] = useState(false);
  const [openRefer, setOpenRefer] = useState(false);
  const [openCall, setOpenCall] = useState(false);
  const [callAppt, setCallAppt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  const loadAll = async () => {
    try {
      const [pr, ar, rr, cr, br] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/appointments?patient_id=${id}`),
        api.get(`/reports?patient_id=${id}`),
        api.get(`/consultations?patient_id=${id}`),
        api.get(`/bills?patient_id=${id}`),
      ]);
      setP(pr.data); setAppts(ar.data); setReports(rr.data); setConsults(cr.data); setBills(br.data);
    } catch { toast.error("Failed to load patient"); }
  };
  useEffect(() => { loadAll(); }, [id]);

  const handleUpload = async (e, category) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const title = prompt("Report title?", file.name);
    if (!title) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("patient_id", id);
    fd.append("category", category);
    fd.append("title", title);
    setUploading(true);
    try {
      await api.post("/reports", fd);
      toast.success("Report uploaded");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const deleteReport = async (rid) => {
    if (!window.confirm("Delete this report?")) return;
    await api.delete(`/reports/${rid}`);
    toast.success("Deleted");
    loadAll();
  };

  const downloadUrl = (storagePath) => {
    const token = localStorage.getItem("mf_token");
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/files/${storagePath}?auth=${encodeURIComponent(token)}`;
  };

  if (!p) return <Layout><div className="py-20 text-center text-[#8A9992]">Loading…</div></Layout>;

  const TABS = [
    { key: "overview", label: "Overview" },
    { key: "reports", label: `Reports (${reports.length})` },
    { key: "consultations", label: `Consultations (${consults.length})` },
    { key: "appointments", label: `Appointments (${appts.length})` },
    { key: "bills", label: `Bills (${bills.length})` },
  ];

  return (
    <Layout>
      <button className="inline-flex items-center gap-2 text-[#47554D] text-sm mb-6 hover:text-[#0A4C36]" onClick={() => nav("/dashboard")} data-testid="back-btn">
        <ArrowLeft size={16} /> Back to patients
      </button>

      {/* Patient header */}
      <div className="card-soft p-8 mb-6" data-testid="patient-header">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#0A4C36]/8 text-[#0A4C36] flex items-center justify-center font-semibold text-xl">
              {initials(p.name)}
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>{p.name}</h1>
              <div className="text-sm text-[#8A9992] mt-1">{p.gender} · {ageDisplay(p)} {p.mobile ? `· ${p.mobile}` : ""}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/consultation/new?patient=${p.id}`} className="btn-emerald" data-testid="start-consult">
              <FileText size={16} /> Start consultation
            </Link>
            <button className="btn-outline-emerald" onClick={() => setOpenBook(true)} data-testid="book-appt-btn">
              <CalendarClock size={16} /> Book appointment
            </button>
            <button className="btn-outline-emerald" onClick={() => setOpenRefer(true)} data-testid="refer-btn">
              <Share2 size={16} /> Refer
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card-soft p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`patient-tab-${t.key}`}
              className={`pill-tab ${tab === t.key ? "pill-tab-active" : "pill-tab-inactive"}`}>{t.label}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Address" value={p.address || "—"} />
            <Field label="Email" value={p.email || "—"} />
            <Field label="Allergies" value={p.allergies || "None recorded"} />
            <Field label="Added on" value={fmtDate(p.created_at)} />
          </div>
        )}

        {tab === "reports" && (
          <div>
            <div className="flex flex-wrap gap-3 mb-6">
              <label className="btn-peach cursor-pointer" data-testid="upload-pathology">
                <Upload size={16} /> Upload pathology
                <input type="file" className="hidden" accept="application/pdf,image/*" onChange={(e) => handleUpload(e, "pathology")} disabled={uploading} />
              </label>
              <label className="btn-peach cursor-pointer" data-testid="upload-radiology">
                <Upload size={16} /> Upload radiology
                <input type="file" className="hidden" accept="application/pdf,image/*" onChange={(e) => handleUpload(e, "radiology")} disabled={uploading} />
              </label>
              <label className="btn-outline-emerald cursor-pointer">
                <Upload size={16} /> Upload other
                <input type="file" className="hidden" onChange={(e) => handleUpload(e, "other")} disabled={uploading} />
              </label>
            </div>
            {reports.length === 0 ? (
              <div className="py-16 text-center text-[#8A9992] text-sm">No reports yet. Upload the first one above.</div>
            ) : (
              <div className="space-y-2">
                {reports.map((r) => (
                  <div key={r.id} className="flex items-center gap-4 rounded-2xl px-4 py-3 row-hover">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${r.category === "pathology" ? "bg-[#FCE7D9] text-[#8A5A34]" : r.category === "radiology" ? "bg-[#E6F0EC] text-[#0A4C36]" : "bg-[#F3F1EC] text-[#47554D]"}`}>
                      <FileText size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-[#1F2A24] text-sm">{r.title}</div>
                      <div className="text-xs text-[#8A9992]">{r.category} · {fmtDateTime(r.created_at)} · {r.original_filename}</div>
                    </div>
                    <a href={downloadUrl(r.storage_path)} target="_blank" rel="noreferrer" className="btn-outline-emerald px-4 py-1.5" data-testid={`report-open-${r.id}`}>
                      <Download size={14} /> Open
                    </a>
                    <button onClick={() => deleteReport(r.id)} className="w-9 h-9 rounded-full hover:bg-red-50 text-red-500 flex items-center justify-center" data-testid={`report-delete-${r.id}`}><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "consultations" && (
          <div className="space-y-3">
            {consults.length === 0 && <div className="py-16 text-center text-[#8A9992] text-sm">No past consultations.</div>}
            {consults.map((c) => (
              <Link key={c.id} to={`/share/prescription/${c.share_token}`} target="_blank" className="block rounded-2xl px-4 py-4 row-hover" data-testid={`consult-row-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-[#1F2A24]">{c.diagnosis || "Consultation"}</div>
                    <div className="text-xs text-[#8A9992] mt-0.5">{fmtDateTime(c.created_at)} · {(c.medications || []).length} meds · {(c.lab_orders || []).length} labs</div>
                  </div>
                  <div className="flex items-center gap-2 text-[#0A4C36] text-sm font-medium">Open <ExternalLink size={14} /></div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {tab === "appointments" && (
          <div className="space-y-2">
            {appts.length === 0 && <div className="py-16 text-center text-[#8A9992] text-sm">No appointments yet.</div>}
            {appts.map((a) => (
              <div key={a.id} className="flex items-center gap-4 rounded-2xl px-4 py-4 row-hover" data-testid={`appt-item-${a.id}`}>
                <div className="w-14 rounded-2xl bg-[#FCE7D9] text-[#5C3A21] flex flex-col items-center justify-center py-2">
                  <div className="text-[10px] tracking-widest uppercase">TIME</div>
                  <div className="text-sm font-semibold">{displayTime(a.time)}</div>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-[#1F2A24]">{fmtDate(a.date)}</div>
                  <div className="text-xs text-[#8A9992]">{a.mode} · {a.status}</div>
                </div>
                <button onClick={() => { setCallAppt(a); setOpenCall(true); }} title="Start video call" className="w-9 h-9 rounded-full bg-[#E6F0EC] hover:bg-[#D3E6DD] text-[#0A4C36] flex items-center justify-center" data-testid={`patient-appt-video-${a.id}`}>
                  <Video size={16} />
                </button>
                <button onClick={async () => { const l = meetingLinkFor(a); if (l) { await navigator.clipboard.writeText(l); toast.success("Meeting link copied"); } }} title="Share meeting link" className="w-9 h-9 rounded-full hover:bg-[#F3F1EC] text-[#47554D] flex items-center justify-center" data-testid={`patient-appt-share-${a.id}`}>
                  <Share2 size={16} />
                </button>
                <Link to={`/consultation/new?patient=${p.id}&appt=${a.id}`} className="btn-outline-emerald px-4 py-1.5"><FileText size={14} /> Consult</Link>
              </div>
            ))}
          </div>
        )}

        {tab === "bills" && (
          <div className="space-y-2">
            {bills.length === 0 && <div className="py-16 text-center text-[#8A9992] text-sm">No bills generated.</div>}
            {bills.map((b) => (
              <Link key={b.id} to={`/share/bill/${b.share_token}`} target="_blank" className="block rounded-2xl px-4 py-4 row-hover" data-testid={`bill-row-${b.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-[#1F2A24]">₹ {b.total?.toFixed(2)}</div>
                    <div className="text-xs text-[#8A9992] mt-0.5">{fmtDateTime(b.created_at)} · {b.items?.length} items</div>
                  </div>
                  <div className="flex items-center gap-2 text-[#0A4C36] text-sm font-medium">Open bill <ExternalLink size={14} /></div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <BookAppointmentDialog open={openBook} onOpenChange={setOpenBook} patient={p} onBooked={loadAll} />
      <ReferPatientDialog open={openRefer} onOpenChange={setOpenRefer} patient={p} onDone={loadAll} />
      <VideoCallDialog
        open={openCall}
        onOpenChange={setOpenCall}
        appointment={callAppt}
        doctorName={user?.name}
        onRoomChange={(room) => { setCallAppt((c) => c ? { ...c, meeting_room: room } : c); loadAll(); }}
      />
    </Layout>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="field-label mb-1">{label}</div>
      <div className="text-sm text-[#1F2A24]">{value}</div>
    </div>
  );
}
